import { iconDataUrl } from '../model/icons';
import { isPlaceholderSource } from '../model/schema';

// One cache for every image the editor paints (photos and recolored icons), shared by the
// stage, the thumbnails and the export. Remote images load with CORS so the canvas stays
// exportable; a source that fails to load is reported, never painted as if it were there.
const entries = new Map();
const listeners = new Set();
let scheduled = false;
// Counts loads (and failures), so renders kept for a layer (render/effects) know an image
// they painted as a placeholder may be there now.
let epoch = 0;
export const resourceEpoch = () => epoch;

const notify = () => {
  epoch += 1;
  if (scheduled) return;
  scheduled = true;
  const flush = () => { scheduled = false; listeners.forEach(listener => listener()); };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(flush); else setTimeout(flush, 0);
};
export const onResourcesChange = listener => { listeners.add(listener); return () => listeners.delete(listener); };
// Something painted from elsewhere is ready (a video frame, render/video): every surface paints again.
export const touchResources = () => notify();

function load(src) {
  const entry = { status: 'loading', image: null, promise: null };
  entry.promise = new Promise(resolve => {
    if (typeof Image === 'undefined') { entry.status = 'error'; resolve(entry); return; }
    const image = new Image();
    if (/^https?:/i.test(src)) image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onload = () => { entry.status = 'ready'; entry.image = image; resolve(entry); notify(); };
    image.onerror = () => { entry.status = 'error'; resolve(entry); notify(); };
    image.src = src;
  });
  entries.set(src, entry);
  return entry;
}

const settledError = { status: 'error', image: null, promise: Promise.resolve({ status: 'error' }) };
export const requestImage = src => (src ? entries.get(src) || load(src) : settledError);
export const readyImage = src => { const entry = requestImage(src); return entry.status === 'ready' ? entry.image : null; };
export const imageStatus = src => (src ? entries.get(src)?.status || 'idle' : 'error');
// Forgets a failed source so the next paint tries it again.
export const retryImage = src => { if (entries.get(src)?.status === 'error') entries.delete(src); return requestImage(src); };

// Icons are rasterized at a size bucket that covers how large they are painted.
const iconBucket = pixels => (pixels <= 256 ? 256 : pixels <= 512 ? 512 : 1024);
export const iconSource = (layer, scale = 1) => iconDataUrl(layer.icon, layer.color, layer.stroke_width, iconBucket(Math.max(layer.width, layer.height) * scale));
// What a layer paints from: a photo, a recolored icon, or the photo a shape is filled with.
export const layerSource = (layer, scale = 1) => (layer.type === 'image' ? layer.source?.url : layer.type === 'icon' ? iconSource(layer, scale)
  : layer.type === 'shape' && layer.fill?.type === 'image' ? layer.fill.source?.url
  : layer.type === 'text' && layer.style?.fill?.type === 'image' ? layer.style.fill.source?.url : null);
const photoOf = layer => (layer.type === 'image' ? layer.source : layer.type === 'shape' && layer.fill?.type === 'image' ? layer.fill.source
  : layer.type === 'text' && layer.style?.fill?.type === 'image' ? layer.style.fill.source : undefined);
// An empty place (a `placeholder` source) is drawn, never loaded.
const paintsImage = layer => layer.type === 'icon' || (photoOf(layer) !== undefined && !isPlaceholderSource(photoOf(layer)));

// Waits for every image and icon a page paints (photo fills of shapes and texts too);
// resolves with the layers that failed.
export async function preparePage(page, scale = 1) {
  const jobs = page.layers.filter(layer => layer.visible && paintsImage(layer)).map(async layer => {
    const settled = await requestImage(layerSource(layer, scale)).promise;
    return settled.status === 'ready' ? null : layer.id;
  });
  return { failed: (await Promise.all(jobs)).filter(Boolean) };
}
